import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Top-level error boundary. Without this, any unhandled render error in a
 * page component kills the whole shell — bottom-nav becomes useless and
 * the user is stuck on a white screen. Wrapping <App/> in this gives them
 * a clear recovery affordance ("Reload") and surfaces the error message
 * for support triage.
 *
 * React error boundaries only catch render-phase errors — async/Promise
 * rejections still need their own try/catch in the calling code. This is
 * the safety net, not the only line of defense.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack)

    // Stale-chunk failures (a lazy import referencing a hash from a
    // previous deploy) surface as RENDER errors via React.lazy, so the
    // window-level chunk recoverer in main.tsx never sees them — the
    // boundary swallowed them and showed "Something went wrong" instead.
    // Same remedy, same one-shot sentinel: hard reload to pull the fresh
    // index.html and its matching chunks. The sentinel (cleared on a
    // successful load) prevents a reload loop when a deploy is genuinely
    // broken.
    const isStaleChunk = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|error loading dynamically imported module/i.test(error.message)
    const sentinel = 'vansales:chunk-recovery'
    if (isStaleChunk && !sessionStorage.getItem(sentinel)) {
      sessionStorage.setItem(sentinel, '1')
      console.warn('[ErrorBoundary] Stale chunk — reloading to pull new bundle.')
      window.location.reload()
    }
  }

  reset = () => {
    this.setState({ error: null })
  }

  reload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="screen-min flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-danger-500 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-4">
            The app hit an unexpected error. Your data is safe — try reloading the page.
          </p>
          <details className="text-left bg-gray-50 rounded-lg p-3 mb-4 text-xs">
            <summary className="cursor-pointer font-medium text-gray-700">Error details</summary>
            <pre className="mt-2 text-danger-600 whitespace-pre-wrap break-words">
              {this.state.error.message}
            </pre>
          </details>
          <div className="flex gap-3">
            <button
              onClick={this.reset}
              className="btn-secondary flex-1"
            >
              Try again
            </button>
            <button
              onClick={this.reload}
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} />
              Reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
